import { Component, OnInit, Input, OnChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { LoginService } from '../services/login.service';
import { GlobalServicesService } from '../services/global-services.service';
import { SharedServicesService } from '../services/shared-services.service';
import { RecaptchaService } from '../services/recaptcha.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { nanoid } from 'nanoid';
import { CAROUSEL } from '../shared/carousel.consts';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    providers: [MessageService],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  @Input() companyLogo: string = '';

  passwordShow: boolean = false;
  height: string = window.innerHeight.toString() + 'px';
  isLoading: boolean = false;
  emailPattern: any = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9]+\.[a-zA-Z]{2,24}$/;
  loginSection = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.pattern(this.emailPattern)]),
    password: new FormControl('', [Validators.required, Validators.minLength(1)])
  });
  invalidCred: boolean = false;
  sourceImg: string = '';
  coverImage: string = '';
  loginEmail: string = '';
  clicked: boolean = false;
  otpRequired: boolean = false;
  otpVerified: boolean = false;
  forgetPassword: boolean = false;
  userDetails: any;
  routingDetails: any;
  privilegeData: any;
  
  ssoProviders: string[] = [];
  ssoLoadingMap: Map<string, boolean> = new Map();
  ssoError: string = '';
  metadataLoaded: boolean = false;
  regularLoginEnabled: boolean = true;
  companyConfig: any = {};

  constructor(
      private loginService: LoginService, 
      private globalService: GlobalServicesService, 
      private messageService: MessageService, 
      private router: Router,
      private route: ActivatedRoute,
      private sharedService: SharedServicesService,
      private recaptchaService: RecaptchaService
    ) {
  }

  ngOnInit(): void {
    this.recaptchaService.initializeRecaptcha().catch(error => {
      console.warn('reCAPTCHA initialization failed:', error);
    });

    this.sharedService.metadata$.subscribe((companyDetails) => {
      if (companyDetails) {
        this.coverImage = companyDetails.cover_image || " ";
        this.sourceImg = companyDetails.logo || " ";
        this.ssoProviders = companyDetails.sso_providers || [];
        this.companyConfig = companyDetails.company_config || {};
        this.regularLoginEnabled = this.companyConfig.regular_login ?? false;
        this.metadataLoaded = true;
        
        if (companyDetails.uuid) {
          localStorage.setItem("companyUUID", companyDetails.uuid);
        }
      }
    });

    this.sharedService.metadataLoading$.subscribe((loading) => {
      if (!loading) {
        if (!this.sharedService.isMetadataLoaded()) {
          let CompanyDetails: any = localStorage.getItem("CompanyDetails") ? JSON.parse(localStorage.getItem("CompanyDetails") || '') : {};
          if (Object.keys(CompanyDetails).length > 0) {
            this.coverImage = CompanyDetails?.cover_image ? CompanyDetails?.cover_image : " ";
            this.sourceImg = CompanyDetails?.logo ? CompanyDetails?.logo : " ";
            this.ssoProviders = CompanyDetails?.sso_providers || [];
            this.companyConfig = CompanyDetails?.company_config || {};
            this.regularLoginEnabled = this.companyConfig.regular_login ?? false;
            this.metadataLoaded = true;
          } else {
            this.sharedService.initiateMetadataRequest(() => this.globalService.metadataFastApi())
              .subscribe({ error: () => { } });
          }
        }
      }
    });

    this.sharedService.metadataError$.subscribe((error) => {
      if (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error
        });
        this.metadataLoaded = true;
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['error'] === 'sso_failed') {
        setTimeout(() => {
          console.log(params['message']);
          this.messageService.add({
            severity: 'error',
            summary: 'SSO Error',
            detail: params['message'] || 'SSO authentication failed. Please try again.'
          });
        }, 500);

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('error');
        currentUrl.searchParams.delete('message');
        window.history.replaceState({}, '', currentUrl.toString());
      }
    });
  }
  
  onResize() {
    this.height = "100vh";
  }

  toggleShowHidePwd() {
    this.passwordShow = !this.passwordShow;
    // let x: any = document.getElementsByClassName("passwordInput")[0];
    // if (x.type === "password") {
    //   x.type = "text";
    //   this.passwordShow = true;
    // } else {
    //   x.type = "password";
    //   this.passwordShow = false;
    // }
  }

  async signIn() {
    this.clicked = true;
    this.loginSection.get('email')?.markAsTouched();
    this.loginSection.get('password')?.markAsTouched();
    
    if (this.loginSection.valid) {
      this.isLoading = true;
      this.invalidCred = false;
      
      try {
        // Wait for reCAPTCHA to be loaded
        await this.recaptchaService.waitForRecaptcha();
        
        // Generate reCAPTCHA token
        const recaptchaToken = await this.recaptchaService.executeRecaptcha('login');
        
        let payload: any = {
          email: this.loginSection.value.email?.toLowerCase(),
          password: this.loginSection.value.password,
          recaptcha_token: recaptchaToken
        }
        
        this.loginService.loginFastApi(payload).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            this.otpRequired = true;
            this.clicked = false;
            this.loginEmail = this.loginSection.value.email || '';
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: response?.body?.detail || "Your OTP has been sent"
            });
          },
          error: (error) => {
            this.invalidCred = true;
            this.isLoading = false;
            this.clicked = false;
            
            // Display backend error message directly
            const errorMessage = error.error?.detail || 'Login failed. Please try again.';
            
            this.messageService.add({
              severity: 'error',
              summary: 'Login Failed',
              detail: errorMessage
            });
            }
          });
          } catch (recaptchaError) {
          this.isLoading = false;
          this.clicked = false;
          
          console.error('reCAPTCHA error:', recaptchaError);
          this.messageService.add({
            severity: 'error',
            summary: 'Security Verification Error',
            detail: 'Unable to verify request. Please refresh the page and try again.'
          });
          }
        }
        }

  initiateSSO(provider: string) {
    this.ssoLoadingMap.set(provider, true);
    this.ssoError = '';
    
    const state = nanoid();
    sessionStorage.setItem('sso_state', state);
    
    const payload = {
      provider_name: provider,
      state: state
    };

    this.loginService.initiateSSOFastApi(payload).subscribe({
      next: (response: any) => {
        this.ssoLoadingMap.set(provider, false);
        if (response.body?.redirect_url) {
          if (response.body?.state) {
            sessionStorage.setItem('sso_state', response.body.state);
          }
          
          window.location.href = response.body.redirect_url;
        } else {
          this.ssoError = 'Failed to initiate SSO. Please try again.';
          this.messageService.add({
            severity: 'error',
            summary: 'SSO Error',
            detail: this.ssoError
          });
        }
      },
      error: (error: any) => {
        this.ssoLoadingMap.set(provider, false);
        this.ssoError = error.error?.detail || 'Failed to initiate SSO. Please try again.';
        this.messageService.add({
          severity: 'error',
          summary: 'SSO Error',
          detail: this.ssoError
        });
      }
    });
  }

  // getSSOProviderIcon(provider: string): string {
  //   switch (provider.toLowerCase()) {
  //     case 'google':
  //       return 'pi pi-google';
  //     case 'microsoft':
  //       return 'pi pi-windows';
  //     case 'github':
  //       return 'pi pi-github';
  //     case 'linkedin':
  //       return 'pi pi-linkedin';
  //     default:
  //       return 'pi pi-sign-in';
  //   }
  // }

  // getSSOProviderLabel(provider: string): string {
  //   switch (provider.toLowerCase()) {
  //     case 'google':
  //       return 'Google';
  //     case 'microsoft':
  //       return 'Microsoft';
  //     case 'github':
  //       return 'GitHub';
  //     case 'linkedin':
  //       return 'LinkedIn';
  //     default:
  //       return `${provider}`;
  //   }
  // }

  isSSOProviderLoading(provider: string): boolean {
    return this.ssoLoadingMap.get(provider) || false;
  }

  ngOnDestroy(): void {
    this.recaptchaService.cleanupRecaptcha();
  }

  navigateToForgotPassword() {
    let loginEmail = "";
    if(this.loginSection.get("email")?.value && this.loginSection.get("email")?.valid) {
      loginEmail = this.loginSection.get("email")?.value || "";
    }
    this.loginService.loginEmail.next(loginEmail);

    this.router.navigate(["/forgotPassword"])
  }
}
